import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Checkout from './Checkout';
import { Spinner } from '@/components/ui/spinner';
import { useLocation } from 'react-router-dom';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { CheckCircle2, X } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import AddressFields from './AddressFields';
import UserInfoForm from './UserInfoForm';
import FileUploadSection from '../features/file-upload/components/FileUploadSection';
import { useDataParser } from '../hooks/useDataParser';
import useFileUploader from '../hooks/useFileUploader';
import { trackEvent } from '../lib/analytics';
import axios from 'redaxios';

export default function Main() {
  const [uploadedFileMeta, setUploadedFileMeta] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionExpiry, setSessionExpiry] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Define autoDetectAddressColumns at top of component
  const autoDetectAddressColumns = useCallback((headers) => {
    const detected = { street: null, city: null, state: null, zip: null };

    detected.street = headers.find((h) =>
      ['address', 'street', 'addr', 'location'].some((k) => h.toLowerCase().includes(k))
    );

    detected.city = headers.find((h) => ['city', 'town'].some((k) => h.toLowerCase().includes(k)));

    detected.state = headers.find((h) => ['state', 'st.'].some((k) => h.toLowerCase().includes(k)));

    detected.zip = headers.find((h) =>
      ['zip', 'zipcode', 'postal', 'postcode'].some((k) => h.toLowerCase().includes(k))
    );

    return detected;
  }, []);

  // 1) Address state
  const [address, setAddress] = useState({
    street: { index: null, value: '' },
    city: { index: null, value: '' },
    state: { index: null, value: '' },
    zip: { index: null, value: '' },
  });

  // 2) User info state
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Track if user manually selected columns
  const [hasUserSelectedColumns, setHasUserSelectedColumns] = useState(false);

  // Required fields for address validation
  const requiredFields = ['street', 'city', 'state', 'zip'];

  // 3) Additional states
  const [isPurchaseDisabled, setIsPurchaseDisabled] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [autoDetectedCount, setAutoDetectedCount] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pendingToast, setPendingToast] = useState(false);

  // 4) Toggles
  const [isSkipTracingSelected] = useState(false);
  const [isZillowTracingSelected] = useState(true);

  // 5) Pricing & location
  const [totalPrice, setTotalPrice] = useState(0);
  const location = useLocation();

  // 6) Data hooks
  const [formError, setFormError] = useState('');
  const [errorMessage, setErrorMessage] = useState(''); // For file-specific errors

  const {
    isLoading,
    file,
    data,
    count,
    errorMessage: fileError,
    parseFile,
    setErrorMessage: setFileError,
    resetParser,
    abortUpload,
  } = useDataParser();

  const {
    uploadToS3,
    uploadError,
    uploadProgress,
    isUploading,
    uploadStats,
    abortUpload: abortFileUpload,
  } = useFileUploader(sessionToken);

  // Auto-detect address columns when data loads
  useEffect(() => {
    if (data.length > 0 && data[0] && !hasUserSelectedColumns) {
      const detected = autoDetectAddressColumns(data[0]);
      const detectedCount = Object.values(detected).filter(Boolean).length;
      setAutoDetectedCount(detectedCount);

      setAddress((prev) => ({
        street: detected.street
          ? {
              value: detected.street,
              index: data[0].indexOf(detected.street),
            }
          : prev.street,
        city: detected.city
          ? {
              value: detected.city,
              index: data[0].indexOf(detected.city),
            }
          : prev.city,
        state: detected.state
          ? {
              value: detected.state,
              index: data[0].indexOf(detected.state),
            }
          : prev.state,
        zip: detected.zip
          ? {
              value: detected.zip,
              index: data[0].indexOf(detected.zip),
            }
          : prev.zip,
      }));

      if (detectedCount > 0) {
        setPendingToast(true);
      }
    }
  }, [data, autoDetectAddressColumns, hasUserSelectedColumns]);

  // Show toast after loading completes
  useEffect(() => {
    if (!file || data.length === 0) return;
    if (!isLoading && pendingToast && autoDetectedCount > 0) {
      toast.custom(
        (t) => (
          <div
            role="alertdialog"
            aria-labelledby="toast-heading"
            aria-describedby="toast-content"
            className="w-full max-w-xl bg-surface border border-border rounded-lg p-4 space-y-3"
          >
            <h2 id="toast-heading" className="sr-only">
              Detected Address Columns
            </h2>
            <div id="toast-content" className="sr-only">
              {`Auto-detected ${autoDetectedCount} out of 4 address columns`}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="font-medium text-primary">
                  Detected {autoDetectedCount}/4 Address Components
                </span>
              </div>
              <button
                onClick={() => {
                  trackEvent('ui.toast.dismissed', { toast: 'auto-detect-address' });
                  toast.dismiss(t);
                }}
                className="text-secondary hover:text-primary ml-auto"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-surface/50 border-b border-border">
                  <tr>
                    <th className="text-left py-3 px-4 text-secondary font-semibold">
                      Column Type
                    </th>
                    <th className="text-left py-3 px-4 text-secondary font-semibold">Mapped To</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(address).map(
                    ([key, valueObj]) =>
                      valueObj.value && (
                        <tr key={key} className="border-b border-border last:border-b-0">
                          <td className="py-2 px-4 text-primary capitalize">{key}</td>
                          <td className="py-2 px-4">
                            <span className="bg-green-500/20 text-green-500 px-2 py-1 rounded-full text-xs font-medium">
                              {valueObj.value}
                            </span>
                          </td>
                        </tr>
                      )
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  trackEvent('ui.toast.dismissed', { toast: 'auto-detect-address' });
                  toast.dismiss(t);
                }}
                className="hover:bg-surface/20"
              >
                Dismiss
              </Button>
            </div>
          </div>
        ),
        {
          duration: Infinity,
          important: true,
        }
      );
    }
  }, [isLoading, pendingToast, autoDetectedCount, address]);

  // [REMOVED the entire effect that auto-uploaded the file]

  const previousFileRef = useRef(null);
  const sessionWarningShownRef = useRef(false);

  // Track pageview
  useEffect(() => {
    trackEvent('page.view', { page: 'main' });
  }, [location]);

  // Reset warning flag when session changes
  useEffect(() => {
    if (sessionToken) {
      sessionWarningShownRef.current = false;
    }
  }, [sessionToken]);

  // Initialize session when component mounts
  useEffect(() => {
    // Skip if session already exists (React StrictMode double-render in dev)
    if (sessionToken) return;

    const initSession = async () => {
      try {
        // Check if we're in development mode
        const isDev =
          window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const enableRealCheckout = import.meta.env.VITE_ENABLE_REAL_CHECKOUT === 'true';

        // Debug logging
        console.log('[Session Debug]', {
          isDev,
          hostname: window.location.hostname,
          enableRealCheckout,
          VITE_ENABLE_REAL_CHECKOUT: import.meta.env.VITE_ENABLE_REAL_CHECKOUT,
          willUseMockSession:
            isDev && !enableRealCheckout && !window.location.href.includes('vercel.app'),
        });

        let data;

        if (isDev && !enableRealCheckout && !window.location.href.includes('vercel.app')) {
          // Development mode - create mock session
          console.log('[DEV] Using mock session for local development');

          // Check for test session duration environment variable
          const testDuration = import.meta.env.VITE_TEST_SESSION_DURATION;
          const sessionDurationSeconds = testDuration ? parseInt(testDuration) : 1800; // Default 30 minutes
          const sessionDurationMs = sessionDurationSeconds * 1000;

          console.log(`[DEV] Session duration: ${sessionDurationSeconds} seconds`);

          data = {
            sessionToken: 'dev-session-' + Math.random().toString(36).substr(2, 9),
            expiresIn: sessionDurationSeconds,
            expiresAt: new Date(Date.now() + sessionDurationMs).toISOString(),
          };
        } else {
          // Production mode - call real API
          const response = await fetch('/api/create-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error('Failed to create session');
          }

          data = await response.json();
        }

        // Store session token in state (not localStorage for security)
        setSessionToken(data.sessionToken);
        setSessionExpiry(new Date(data.expiresAt));

        // Note: redaxios doesn't support interceptors like axios
        // In production, the backend proxy will handle authentication

        trackEvent('security.session.created');
      } catch (error) {
        console.error('Session initialization failed:', error);
        console.error('[Session Error Details]', {
          message: error.message,
          stack: error.stack,
          isDev:
            window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1',
          enableRealCheckout: import.meta.env.VITE_ENABLE_REAL_CHECKOUT,
          url: window.location.href,
        });
        toast.error('Failed to initialize secure session. Please refresh the page.');
        trackEvent('security.session.error', { error: error.message });
      }
    };

    initSession();
  }, [sessionToken]);

  // Check session expiry periodically
  useEffect(() => {
    if (!sessionExpiry) return;

    // Determine if we're in test mode
    const testDuration = import.meta.env.VITE_TEST_SESSION_DURATION;
    const isTestMode = testDuration && parseInt(testDuration) < 300; // Less than 5 minutes

    const checkExpiry = setInterval(() => {
      // Skip all checks if session already expired
      if (sessionExpired) {
        clearInterval(checkExpiry);
        return;
      }

      const now = new Date();
      const timeLeft = sessionExpiry - now;

      // Check if session has expired
      if (timeLeft <= 0) {
        toast.error('Session expired. Please refresh the page to continue.');
        trackEvent('security.session.expired');
        resetParser();
        setSessionExpired(true);
        clearInterval(checkExpiry);
        return;
      }

      // Only show warnings if session hasn't expired and warning hasn't been shown
      if (!sessionExpired && !sessionWarningShownRef.current) {
        if (isTestMode) {
          // Test mode: warn 10 seconds before expiry (only once)
          if (timeLeft < 10 * 1000 && timeLeft > 0) {
            toast.warning('Your session will expire in 10 seconds!');
            sessionWarningShownRef.current = true;
            trackEvent('security.session.warning', { secondsLeft: 10 });
          }
        } else {
          // Normal mode: warn 5 minutes before expiry (only once)
          if (timeLeft < 5 * 60 * 1000) {
            toast.warning('Your session will expire in 5 minutes. Please complete your upload.');
            sessionWarningShownRef.current = true;
            trackEvent('security.session.warning', { minutesLeft: 5 });
          }
        }
      }
    }, 1000); // Check every second for precise timing

    return () => clearInterval(checkExpiry);
  }, [sessionExpiry, sessionExpired]);

  // Compute total price
  const allAddressFieldsSelected = useMemo(
    () => requiredFields.every((field) => address[field].value),
    [address]
  );

  // Email validation helper
  const isValidEmail = useCallback((email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email?.trim() || '');
  }, []);

  // Name validation helper (at least 2 chars, letters only)
  const isValidName = useCallback((name) => {
    const trimmed = name?.trim() || '';
    return trimmed.length >= 2 && /^[a-zA-Z\s'-]+$/.test(trimmed);
  }, []);

  useEffect(() => {
    const basePrice = 5;
    const costPerUnit = 0.04;
    const skipTracingCostPerUnit = 0.08;
    const quantity = count > 0 ? count : 0;

    const zillowCost = isZillowTracingSelected ? quantity * costPerUnit + basePrice : 0;

    const skipCost = isSkipTracingSelected ? quantity * skipTracingCostPerUnit + basePrice : 0;

    setTotalPrice(zillowCost + skipCost);

    const isCountInvalid = quantity > 100000;
    const areFieldsMissing =
      !allAddressFieldsSelected || !userInfo.firstName || !userInfo.lastName || !userInfo.email;
    const hasInvalidFields =
      !isValidName(userInfo.firstName) ||
      !isValidName(userInfo.lastName) ||
      !isValidEmail(userInfo.email);

    setIsPurchaseDisabled(
      isCountInvalid ||
        areFieldsMissing ||
        hasInvalidFields ||
        isLoading ||
        data.length === 0 ||
        sessionExpired ||
        !sessionToken
    );
  }, [
    count,
    isSkipTracingSelected,
    isZillowTracingSelected,
    allAddressFieldsSelected,
    userInfo,
    isLoading,
    data,
    sessionExpired,
    sessionToken,
    isValidEmail,
    isValidName,
  ]);

  const handleFileChange = useCallback(
    (file) => {
      // Clear both error states on any file change attempt
      setFormError('');
      setFileError('');

      // Handle file cancellation/reset
      if (!file) {
        resetParser();
        setAddress({
          street: { index: null, value: '' },
          city: { index: null, value: '' },
          state: { index: null, value: '' },
          zip: { index: null, value: '' },
        });
        setHasUserSelectedColumns(false);
        setPendingToast(false);
        setAutoDetectedCount(0);
        previousFileRef.current = null;
        return;
      }

      if (file?.error) {
        setFormError(file.error);
        setFileError(file.error);
        setHasUserSelectedColumns(false);
      } else if (file && !(file instanceof File)) {
        return;
      } else if (file?.name.match(/\.(csv|xlsx|xls)$/i)) {
        if (
          previousFileRef.current &&
          file.name === previousFileRef.current.name &&
          file.size === previousFileRef.current.size
        ) {
          console.log(
            '[DEBUG] handleFileChange: same file selected, skipping parse & address reset'
          );
          return;
        }
        parseFile(file);
        setAddress({
          street: { index: null, value: '' },
          city: { index: null, value: '' },
          state: { index: null, value: '' },
          zip: { index: null, value: '' },
        });
        setHasUserSelectedColumns(false);
      }
    },
    [parseFile, setFileError, resetParser]
  );

  function displaySelectedData() {
    if (!file || !data.length) return null;
    const indices = {
      street: data[0].indexOf(address.street.value),
      city: data[0].indexOf(address.city.value),
      state: data[0].indexOf(address.state.value),
      zip: data[0].indexOf(address.zip.value),
    };
    return data.slice(1, 6).map((row, idx) => (
      <p className="text-sm text-primary text-left" key={idx}>
        {`${row[indices.street] || ''}, ${row[indices.city] || ''}, ${
          row[indices.state] || ''
        }, ${row[indices.zip] || ''}`}
      </p>
    ));
  }

  // Final upload
  async function handleS3Upload() {
    try {
      if (
        !address.street.value ||
        !address.city.value ||
        !address.state.value ||
        !address.zip.value
      ) {
        setFormError('Please select columns for Street, City, State, and Zip.');
        return false;
      }

      // 1) Build the new CSV with appended "full-address"
      const newData = data.map((row, index) => {
        if (index === 0) {
          return [...row, 'full-address'];
        } else {
          const streetVal = row[address.street.index] ?? '';
          const cityVal = row[address.city.index] ?? '';
          const stateVal = row[address.state.index] ?? '';
          const zipVal = row[address.zip.index] ?? '';
          const fullAddress = `${streetVal}, ${cityVal}, ${stateVal}, ${zipVal}`;
          return [...row, fullAddress];
        }
      });

      const csvHeaders = newData[0];
      const csvRows = newData.slice(1);
      const csvContent = [csvHeaders, ...csvRows]
        .map((arr) => arr.map((val) => `"${(val || '').toString().replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const csvFile = new File([csvContent], 'converted.csv', {
        type: 'text/csv',
      });

      // 2) Upload to S3
      const { statusCode, userId, filePattern } = await uploadToS3(csvFile, userInfo);
      if (statusCode >= 200 && statusCode < 300) {
        setFormError('');
        setUploadedFileMeta({ userId, filePattern });
        return true;
      } else {
        setFormError('Upload failed, please try again.');
        return false;
      }
    } catch (err) {
      setFormError(`Upload error: ${err.message}`);
      return false;
    }
  }

  const PricingTable = ({ count, totalPrice }) => (
    <div className="relative overflow-x-auto w-full max-w-4xl mt-8 mb-6">
      <div className="bg-surface/10 p-4 rounded-lg mb-4">
        <table className="w-full text-base text-left border-collapse">
          <thead className="text-sm uppercase text-secondary">
            <tr>
              <th scope="col" className="px-6 py-4">
                Service
              </th>
              <th scope="col" className="px-6 py-4 text-right">
                Rows
              </th>
              <th scope="col" className="px-6 py-4 text-right">
                Price
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border-color">
              <td className="px-6 py-4 font-medium text-primary">Zillow Property Details</td>
              <td className="px-6 py-4 text-primary text-right">{count.toLocaleString()}</td>
              <td className="px-6 py-4 text-primary text-right font-semibold">
                ${totalPrice.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {count > 100000 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 p-4 rounded-lg text-sm">
          Note: For requests over 100,000 rows, please contact our support team for enterprise
          pricing.
        </div>
      )}
    </div>
  );

  const UploadAlert = ({ isOpen, onOpenChange }) => (
    <AlertDialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <AlertDialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-6 rounded-lg shadow-lg z-50 bg-surface w-[90%] max-w-sm">
          <AlertDialog.Title className="text-xl font-bold mb-3 text-primary">
            File Uploaded
          </AlertDialog.Title>
          <AlertDialog.Description className="text-secondary mb-4 text-sm">
            We have added a new Skip Tracing service. If only property details are needed, please
            uncheck the Skip Tracing toggle.
          </AlertDialog.Description>
          <div className="flex justify-end">
            <AlertDialog.Action asChild>
              <Button className="purchase-button">OK</Button>
            </AlertDialog.Action>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );

  return (
    <div className="relative min-h-screen w-full">
      {isLoading && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center space-y-4">
            <Spinner className="w-16 h-16" />
            <p className="text-primary-foreground font-medium">Processing your file...</p>
          </div>
        </div>
      )}

      {/* Session Expired Overlay */}
      {sessionExpired && (
        <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center">
          <Card className="max-w-md mx-4 p-8 text-center space-y-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-primary">Session Expired</h2>
              <p className="text-secondary">
                Your secure session has expired for your protection. Please refresh the page to
                start a new session.
              </p>
            </div>
            <div className="space-y-3">
              <Button onClick={() => window.location.reload()} className="w-full" size="lg">
                Refresh Page
              </Button>
              <p className="text-xs text-secondary">
                Sessions expire after 30 minutes of inactivity
              </p>
            </div>
          </Card>
        </div>
      )}
      <div className="px-8 py-12 mx-auto">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-primary text-left mb-2">
            Property Details Bulk Upload Tool
          </h2>
          <h3 className="text-base md:text-lg text-secondary font-medium mb-8 text-left">
            Get property details for a list of addresses
          </h3>

          {/* Step 1: Upload */}
          <FileUploadSection
            file={file}
            errorMessage={fileError}
            handleFileChange={handleFileChange}
            uploadProgress={uploadProgress}
            isUploading={isUploading}
            uploadStats={uploadStats}
            abortUpload={abortFileUpload}
          />

          {/* [REMOVED the "Upload & Continue" button and the error message row] */}
        </div>

        {!file && (
          <div className="max-w-3xl mx-auto mt-12">
            {/* Big headline */}
            <h1 className="text-2xl md:text-3xl font-bold text-left mb-4 text-primary">
              CSV File Data Enrichment
            </h1>
            {/* Intro paragraph */}
            <p className="text-sm text-secondary/90 mb-8 text-left leading-relaxed">
              Our bulk upload tool lets you quickly extract property details from a CSV or Excel
              file. No subscription is required—just a one-time payment based on the number of rows
              you upload. Whether you're a real estate professional, investor, or researcher, this
              tool streamlines data collection, saving you hours of manual effort.
            </p>

            {/* How to Use the Application */}
            <h2 className="text-xl font-bold text-left mb-4 text-primary">
              How to Use the Application
            </h2>
            <ol className="list-decimal pl-6 [counter-reset:item] marker:text-primary marker:font-normal space-y-4 text-secondary/90 text-base text-left">
              <li>
                <strong>Upload Your File:</strong> Begin by selecting your CSV or Excel (XLS/XLSX)
                file. We'll parse it for valid addresses.
              </li>
              <li>
                <strong>Map Columns:</strong> Confirm which columns correspond to Street, City,
                State, and ZIP. This ensures accurate data matching.
              </li>
              <li>
                <strong>Preview & Verify:</strong> Get a quick sample to ensure the addresses look
                correct before finalizing.
              </li>
              <li>
                <strong>Enter Details & Purchase:</strong> Provide your contact info, see your total
                cost, and proceed with a single payment—no ongoing subscription.
              </li>
              <li>
                <strong>Download Results:</strong> After processing, we'll let you send you an email
                link to download your results.
              </li>
            </ol>
          </div>
        )}

        {/* Step 2: Pricing & Select Columns */}
        <div className="max-w-3xl mx-auto">
          {file && <PricingTable count={count} totalPrice={totalPrice} />}
          {file && (
            <div className="w-full bg-blue-500/10 border border-blue-500/20 text-blue-500 rounded-lg px-4 py-3 mb-6 text-sm">
              Tip: Including column headers in your file ensures accurate data processing
            </div>
          )}
          {file && data.length > 0 && (
            <>
              <h4 className="text-xl font-semibold text-left mb-4 text-primary">
                Step 2: Select CSV Columns
              </h4>
              <AddressFields
                address={address}
                setAddress={setAddress}
                data={data}
                onUserChangedAddressColumns={() => setHasUserSelectedColumns(true)}
              />
              {data.length > 1 &&
                address.street.value &&
                address.city.value &&
                address.state.value &&
                address.zip.value && (
                  <div className="mt-8">
                    <label className="block text-left text-sm font-semibold text-primary mb-3">
                      Preview of Selected Addresses:
                    </label>
                    <div className="p-4 bg-surface/10 rounded-lg border border-border-color">
                      {displaySelectedData()}
                    </div>
                  </div>
                )}
            </>
          )}
        </div>

        {/* Step 3: User Info & Checkout */}
        {file && data.length > 0 && (
          <div className="max-w-3xl mx-auto space-y-6">
            <UserInfoForm
              userInfo={userInfo}
              setUserInfo={setUserInfo}
              errorMessage={formError}
              uploadError={uploadError}
              handleS3Upload={handleS3Upload}
              isPurchaseDisabled={isPurchaseDisabled}
            />

            {/* Now pass 'uploadedFileMeta' to Checkout */}
            <Checkout
              isPurchaseDisabled={isPurchaseDisabled}
              data={data}
              address={address}
              userInfo={userInfo}
              setUploadedFileMeta={setUploadedFileMeta}
              totalPrice={totalPrice}
              skipTracingService={isSkipTracingSelected}
              zillowService={isZillowTracingSelected}
              sessionToken={sessionToken}
            />
          </div>
        )}

        <UploadAlert isOpen={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen} />
      </div>
    </div>
  );
}
